import React from 'react';

import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { Button, SxProps, Theme } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import axios from '../axios';
import { showOnboardNotification } from '../utils/notificationUtils';
import { safeRedirect } from '../utils/urlUtils';

export enum LinkPage {
  Approve = 'Approve',
  SelectTSP = 'Select TSP',
  OnboardingCreds = 'Onboarding Credentials',
  Motive = 'Motive Fail',
}

export enum ButtonVariant {
  Icon = 'icon',
  Text = 'text',
}

type BaseButtonExitLinkProps = {
  token: string;
  redirectURIError: string;
  serviceName: string;
  pageOnExit: LinkPage;
  selectedTSP?: string;
  skipReasonParam?: string;
  skipTspParam?: string;
};

type IconButtonExitLinkProps = BaseButtonExitLinkProps & {
  styles?: SxProps<Theme> | undefined;
  variant?: ButtonVariant;
  label?: string;
  onBeforeExit?: () => void;
};

export const ButtonExitLink: React.FC<IconButtonExitLinkProps> = ({
  token,
  redirectURIError,
  serviceName,
  pageOnExit,
  selectedTSP,
  styles,
  variant = ButtonVariant.Icon,
  label = 'SKIP LINKING TELEMATICS',
  onBeforeExit,
}) => {
  const theme = useTheme();

  const isIconVariant = variant === ButtonVariant.Icon;

  const handleClick = () => {
    // Call the callback if provided
    if (onBeforeExit) {
      onBeforeExit();
    }

    // Handle the exit/redirect
    handleLeaveLink({
      token,
      redirectURIError,
      serviceName,
      pageOnExit,
      selectedTSP,
    });
  };

  return (
    <>
      <Button
        variant={isIconVariant ? 'text' : 'text'}
        sx={{
          textTransform: isIconVariant ? 'none' : 'uppercase',
          fontSize: isIconVariant ? '12px' : '14px',
          fontWeight: isIconVariant ? 'normal' : 'bold',
          color: theme.palette.primary.main,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: alpha(
              theme.palette.primary.main,
              theme.palette.action.hoverOpacity
            ),
          },
          '&:focus': {
            backgroundColor: alpha(
              theme.palette.primary.main,
              theme.palette.action.focusOpacity || 0.12
            ),
          },
          ...styles,
        }}
        onClick={handleClick}
      >
        {isIconVariant ? <ExitToAppIcon /> : label}
      </Button>
    </>
  );
};

export async function handleLeaveLink({
  token,
  redirectURIError,
  serviceName,
  pageOnExit,
  selectedTSP,
  skipReasonParam,
  skipTspParam,
}: BaseButtonExitLinkProps) {
  // Note: Webhook handling is now done by the analytics system before calling this function
  // This function only handles the redirect/exit logic

  if (redirectURIError && redirectURIError !== 'null') {
    // Legacy exit call for backward compatibility
    axios
      .post('/service/exit', {
        service: serviceName,
        pageName: pageOnExit,
        selected_tsp: selectedTSP || '',
      })
      .then(() =>
        safeRedirect(redirectURIError, undefined, {
          skip_reason: skipReasonParam,
          skip_tsp_name: skipTspParam,
        })
      )
      .catch((error) => {
        console.log(
          'An error happened while triggering error callback.',
          error
        );
        // Still redirect even if there's an error with the abandoned call
        setTimeout(() => {
          safeRedirect(redirectURIError, undefined, {
            skip_reason: skipReasonParam,
            skip_tsp_name: skipTspParam,
          });
        }, 1000);
      });
  } else {
    // No valid redirect URI - this is likely a new window scenario
    try {
      // Try to close the window first
      window.close();

      // If window.close() didn't work (popup blockers, etc.),
      // show a message after a brief delay
      setTimeout(() => {
        showOnboardNotification('Skipped');
      }, 100);
    } catch (error) {
      // Fallback: show close message
      showOnboardNotification('Skipped');
    }
  }
}

export const ExitButtonTSPPageStyles = {
  position: 'absolute',
  right: '0',
  top: '50%',
  transform: 'translate(0, -50%)',
};

export const ExitButtonApprovePageStyles = {
  position: 'absolute',
  top: 40,
  right: 20,
};
